
import { TranslateResult } from 'vue-i18n';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ConsentToggleButton from '@/components/consent/ConsentToggleButton.vue';

import { CollectionType } from '@/enums/collection-type';
import { SnowplowTitleContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import Provider from '@/interfaces/provider';
import { FilterName } from '@/stores/filter.store';

import { Maybe } from '@/@types/graphql-types';
import { TopArticleFragment } from '@/pages/graphql/queries/GetTopArticles.query';
import { TopTitleFragment } from '@/pages/graphql/queries/GetTopTitles.query';
import { COUNTRY_WITHOUT_TITLEDETAIL } from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';

const Filter = namespace('filter');

type TopTitles = {
	movie: TopTitleFragment[];
	upcomingMovie: TopTitleFragment[];
	show: TopTitleFragment[];
	upcomingShow: TopTitleFragment[];
};

const routesWithTitleList = [
	CollectionType.NEW,
	CollectionType.POPULAR,
	CollectionType.SEARCH,
	CollectionType.MY_LISTS,
	CollectionType.PUBLIC_LISTS,
	CollectionType.TV_SHOW_TRACKING,
];

@Component({
	name: 'AppDefaultFooter',
	components: {
		ConsentToggleButton,
	},
	serverCacheKey: (props: {
		locale: string;
		topTitles: TopTitles;
		topArticles: TopArticleFragment[];
		providers: () => Provider[];
	}) =>
		[
			// locale
			props.locale,
			// providers
			props
				.providers()
				.map(({ shortName }) => shortName)
				.join('-'),
			// topTitles
			Object.values(props.topTitles)
				.flat()
				.map(({ id }) => id)
				.join('-'),
			// topTitles
			Object.values(props.topArticles)
				.map(({ id }) => id)
				.join('-'),
		].join('_'),
})
export default class AppDefaultFooter extends Vue {
	CollectionType = CollectionType;

	@Prop({ required: true }) locale: string;
	@Prop({ required: true }) webLocale: WebLocale;
	@Prop({ required: true }) topTitles: TopTitles;
	@Prop({ required: true }) topArticles: TopArticleFragment[];
	@Prop({ default: () => [] }) providers: () => Provider[];

	@Filter.Action setFilterValue: (payload: {
		filterName: FilterName;
		filterValue: any;
		collectionType: CollectionType;
		updateRouteState: boolean;
	}) => void;

	providersLimit = 5;

	currentYear = new Date().getFullYear();

	get version() {
		return process.env.VERSION;
	}

	get showAppFooter() {
		const currentCollection: CollectionType = this.$route.meta?.collectionType;
		return !routesWithTitleList.includes(currentCollection);
	}

	get latestEditorialHeader() {
		return this.$t('WEBAPP_FOOTER_TOP_X_NEWS', { x: this.topArticles.length });
	}

	get shouldLinkToUpcoming() {
		return this.$router.getRoutes().some(({ name }) => name === 'app.titles.upcoming.list');
	}

	get titleUrlsEnabled() {
		return !COUNTRY_WITHOUT_TITLEDETAIL.includes(this.webLocale);
	}

	handleTopProvider(provider: Provider, collectionType: CollectionType, index: number) {
		const translatable =
			collectionType === CollectionType.POPULAR
				? 'WEBAPP_FOOTER_TOP_X_PROVIDERS'
				: 'WEBAPP_FOOTER_TOP_X_NEW_ON_PROVIDER';

		this.trackProviderClick(provider, this.$t(translatable, { x: this.providersLimit }), index);
		this.setProvider(provider, collectionType);
	}

	parseArticleTitle(title: Maybe<string> | undefined) {
		if (!title) return '';

		const match = title.match(/<h1[^>]*>(.*?)<\/h1>/);
		return match ? match[1] : '';
	}

	trackHeadingClick(headline: TranslateResult) {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'tabchange',
			label: `${headline}`,
			property: 'footer',
			useLastRoute: true,
		});
	}

	trackProviderClick(provider: Provider, headline: TranslateResult, value: number) {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'tabchange',
			label: `${headline},${provider.packageId}`,
			value,
			property: 'footer',
			useLastRoute: true,
		});
	}

	trackClick(item: TopTitleFragment | TopArticleFragment, headline: TranslateResult, value: number) {
		const contexts = [];

		if (item.__typename !== 'Article') {
			contexts.push(new SnowplowTitleContext(item.objectId, item.objectType));
		}
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'title_clicked',
				label: headline.toString(),
				value,
				property: 'footer',
				useLastRoute: true,
			},
			contexts
		);
	}

	setProvider(provider: Provider, collectionType: CollectionType) {
		this.setFilterValue({
			filterName: 'providers',
			filterValue: [provider.shortName],
			collectionType,
			// prevents duplicate navigation
			updateRouteState: false,
		});
	}
}
